import { useEffect, type ReactNode } from 'react'
import { QueryClientProvider } from '@tanstack/react-query'
import { MotionConfig } from 'framer-motion'
import { Toaster } from 'sonner'
import { queryClient } from '@/lib/query-client'
import { SessionProvider } from '@/app/providers/SessionProvider'
import { ErrorBoundary } from '@/components/common/ErrorBoundary'
import { LoadingOverlay } from '@/components/common/LoadingOverlay'
import { TooltipProvider } from '@/components/ui/tooltip'
import { useAuthStore } from '@/store/auth.store'
import { useUiStore } from '@/store/ui.store'
import { usePermissionStore } from '@/store/permission.store'
import { dismissBootSplash } from '@/utils/boot-splash'

function AuthHydrationGate({ children }: { children: ReactNode }) {
  const hasPersisted = useAuthStore.persist.hasHydrated()
  const isHydrated = useAuthStore((s) => s.isHydrated)
  const setHydrated = useAuthStore((s) => s.setHydrated)
  const hydrateFromToken = useAuthStore((s) => s.hydrateFromToken)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const role = useAuthStore((s) => s.session?.user.role)
  const hasPermission = useAuthStore((s) => s.hasPermission)
  const fetchPermissions = usePermissionStore((s) => s.fetch)

  const canLoadRoleDefaults =
    isAuthenticated &&
    (role === 'super_admin' || hasPermission('permissions.view'))

  useEffect(() => {
    const theme = useUiStore.getState().theme
    document.documentElement.classList.toggle('dark', theme === 'dark')
  }, [])

  useEffect(() => {
    if (!hasPersisted) return

    let cancelled = false
    ;(async () => {
      try {
        await hydrateFromToken()
        const state = useAuthStore.getState()
        const canFetch =
          state.isAuthenticated &&
          (state.session?.user.role === 'super_admin' ||
            state.hasPermission('permissions.view'))
        if (!cancelled && canFetch) {
          void fetchPermissions().catch(() => undefined)
        }
      } finally {
        if (!cancelled) setHydrated(true)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [hasPersisted, hydrateFromToken, setHydrated, fetchPermissions])

  useEffect(() => {
    if (isHydrated && canLoadRoleDefaults) {
      void fetchPermissions().catch(() => undefined)
    }
  }, [isHydrated, canLoadRoleDefaults, fetchPermissions])

  useEffect(() => {
    if (!isHydrated) return
    let cancelled = false
    const frame = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        if (!cancelled) dismissBootSplash()
      })
    })
    return () => {
      cancelled = true
      window.cancelAnimationFrame(frame)
    }
  }, [isHydrated])

  return children
}

export function AppProviders({ children }: { children: ReactNode }) {
  return (
    <QueryClientProvider client={queryClient}>
      <SessionProvider>
        <TooltipProvider delayDuration={250}>
          <ErrorBoundary>
            {/* App-wide: Framer Motion honors OS `prefers-reduced-motion` for every
                animation (opacity stays; x/y/scale/rotate/layout collapse to instant).
                Components that already gate manually via `usePrefersReducedMotion`
                are unaffected — this just closes the gap for shell chrome (sidebar,
                nav) that doesn't have its own check. */}
            <MotionConfig reducedMotion="user">
              <AuthHydrationGate>
                {children}
                <LoadingOverlay />
                <Toaster
                  position="top-right"
                  richColors
                  closeButton
                  // Phones: below the app bar, never over it.
                  mobileOffset={{ top: 'calc(3.5rem + env(safe-area-inset-top, 0px) + 0.5rem)' }}
                  toastOptions={{ duration: 3500 }}
                />
              </AuthHydrationGate>
            </MotionConfig>
          </ErrorBoundary>
        </TooltipProvider>
      </SessionProvider>
    </QueryClientProvider>
  )
}
